/**
 * The one way a text editor's edit reaches its Y.Text (#987 — it replaces the
 * copy each host kept: HtmlEditor, MarkdownEditor, ScriptEditor).
 *
 * The editor reports the edit itself — its changes against the text it held
 * (`edits.oldText`, always LF: CodeMirror's doc is). Those changes are written
 * as they are, so a format's hunks stay separate small edits and collaborators'
 * carets move only where text changed:
 *
 *  - a document holding `\r` (a CRLF file) first loses just those characters, as
 *    small deletes — the editor works in LF, so the file becomes LF on its first
 *    edit, as it did with the textarea editor;
 *  - when the document moved since the editor last saw it (a collaborator's edit
 *    still on its way to the editor), the edit is REBASED over that move instead
 *    of diffing the whole text, which would undo the collaborator's edit.
 *
 * Without `edits` (an older caller) the whole new text is diffed as one span.
 * Everything goes in one transaction: one undo step. A format (`edits.isolate`)
 * is also kept apart from the typing around it (`stopCapturing`).
 */
import { ChangeSet } from '@codemirror/state'
import type * as Y from 'yjs'
import type { EditorTextChange } from './text-modifier-types.js'
import { formatHunks } from './format/format-hunks.js'
import { changesSince } from './ytext-change-log.js'

/** The fallback diff's budget (lines changed): beyond it, one span. */
const MAX_REBASE_EDITS = 200

/** `\r\n` and lone `\r` → `\n`. */
export function toLf(text: string): string {
  return text.includes('\r') ? text.replace(/\r\n?/g, '\n') : text
}

/** The one span that turns `a` into `b`. */
function oneSpan(a: string, b: string): { from: number; to: number; insert: string } {
  let start = 0
  const max = Math.min(a.length, b.length)
  while (start < max && a.charCodeAt(start) === b.charCodeAt(start)) start++
  let endA = a.length
  let endB = b.length
  while (endA > start && endB > start && a.charCodeAt(endA - 1) === b.charCodeAt(endB - 1)) { endA--; endB-- }
  return { from: start, to: endA, insert: b.slice(start, endB) }
}

/**
 * Delete every `\r` (a lone one becomes `\n`), back to front, as small edits. Call it inside the
 * transaction of ANY write whose offsets come from the editor (its caret / selection are LF offsets):
 * on a CRLF document they'd land one character early per `\r` before them.
 */
export function crToLf(ytext: Y.Text): void {
  const text = ytext.toString()
  for (let i = text.length - 1; i >= 0; i--) {
    if (text.charCodeAt(i) !== 13) continue
    ytext.delete(i, 1)
    if (text[i + 1] !== '\n') ytext.insert(i, '\n')
  }
}

export function writeEditorChange(
  doc: Y.Doc,
  ytext: Y.Text,
  e: React.ChangeEvent<HTMLTextAreaElement>,
  opts: { stopCapturing?: () => void } = {},
): void {
  const edits = (e as Partial<EditorTextChange>).edits
  if (!edits) {
    const span = oneSpan(ytext.toString(), e.target.value)
    doc.transact(() => {
      if (span.to > span.from) ytext.delete(span.from, span.to - span.from)
      if (span.insert) ytext.insert(span.from, span.insert)
    })
    return
  }
  if (edits.isolate) opts.stopCapturing?.()
  doc.transact(() => {
    const base = edits.oldText()
    // Asked before anything is written in this transaction: the log ends at the text the Y.Text holds now.
    const logged = changesSince(ytext, base)
    if (ytext.toString().includes('\r')) crToLf(ytext)
    const now = ytext.toString()
    let changes = ChangeSet.of(edits.changes, base.length)
    if (now !== base) {
      // The document moved under the editor: carry the edit over that move. Exactly, from the Y.Text's own change
      // log; else (a CRLF conversion just now, an untracked text) from a diff — capped, so a large reformatted
      // file never stalls a keystroke: past the cap, one span (an edit inside it lands at its edge).
      const fromLog = logged && logged.newLength === now.length ? logged : null // a CRLF conversion just now: not logged
      const remote = fromLog ?? ChangeSet.of(formatHunks(base, now, { maxEditLength: MAX_REBASE_EDITS, chars: true }) ?? [oneSpan(base, now)], base.length)
      changes = changes.map(remote)
    }
    const list: Array<{ from: number; to: number; insert: string }> = []
    changes.iterChanges((fromA, toA, _fromB, _toB, inserted) => { list.push({ from: fromA, to: toA, insert: inserted.toString() }) })
    // Back to front: each change's positions are in the text before it, untouched by the ones after it.
    for (let i = list.length - 1; i >= 0; i--) {
      const c = list[i]!
      if (c.to > c.from) ytext.delete(c.from, c.to - c.from)
      if (c.insert) ytext.insert(c.from, c.insert)
    }
  })
  if (edits.isolate) opts.stopCapturing?.()
}
